/**
 * ImportReviewSheet — bottom-anchored review surface for a chat import (spec
 * §3.2 step 5-7). A pinned "Needs attention (n)" section lists flagged rows so
 * they can never strand behind a tab, then Positions/Trades tabs of collapsed
 * rows that tap-expand into edit fields. Update (⇄) rows show the semantic
 * diff (existing→new) plus the correction-vs-new-lot resolution toggle, and
 * any included update row arms the H2 confirmation gate before Apply commits
 * through postImportSave.
 *
 * Same direct-manipulation pattern as the thesis sheet (spec §4.5): every edit
 * updates local state optimistically AND fires postImportEdit fire-and-forget
 * (a failure surfaces as a non-blocking inline line — the transcript fold on
 * reload is the recovery path, not the edit round-trip). Include flags are
 * sheet-local and re-seeded from the suggestion defaults on each open: new +
 * update rows included, match/needs_edit/dupe excluded.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { flaggedCount } from '../../services/importPreview';
import type { ImportPreview, ImportPreviewRow } from '../../services/importPreview';
import { postImportEdit, postImportSave, postImportUndo } from '../../services/chatApi';
import type { ImportRowPatch, ImportSaveRow, ImportSaveOutcome } from '../../services/chatApi';

type Resolution = 'add' | 'replace';
type Suggestion = ImportPreviewRow['suggestion'];

/** The allowlisted patch keys the user can edit here — instrument_id is owned
 * by symbol resolution, resolution_default by the dedicated toggle. */
type EditableField = 'symbol' | 'quantity' | 'price' | 'currency' | 'date';
const EDIT_FIELDS: EditableField[] = ['symbol', 'quantity', 'price', 'currency', 'date'];

type RowDraft = Record<EditableField, string>;
type RowState = ImportPreviewRow & { include: boolean; resolution: Resolution };

const CHIP_ICON: Record<Suggestion, string> = {
  new: '➕',
  update: '⇄',
  match: '✓',
  needs_edit: '⚠',
  dupe: '✓',
};
const CHIP_DEFAULT: Record<Suggestion, string> = {
  new: 'New',
  update: 'Update',
  match: 'Match',
  needs_edit: 'Needs edit',
  dupe: 'Already in book',
};

/** Carry-forward rule (importPreview replay semantics): the include flag is
 * sheet-local and initializes from the suggestion default on every open. */
function seedRows(preview: ImportPreview): RowState[] {
  return preview.rows.map((r) => ({
    ...r,
    include: r.suggestion === 'new' || r.suggestion === 'update',
    resolution: r.resolution_default ?? 'replace',
  }));
}

function seedDraft(r: ImportPreviewRow): RowDraft {
  return {
    symbol: r.symbol ?? '',
    quantity: r.quantity != null ? String(r.quantity) : '',
    price: r.price != null ? String(r.price) : '',
    currency: r.currency ?? '',
    date: r.date ?? '',
  };
}

const fmtQty = (v: number | null): string => (v != null ? String(v) : '?');
const fmtDelta = (v: number | null): string => (v == null ? '?' : `${v >= 0 ? '+' : ''}${v}`);

export function ImportReviewSheet({
  visible,
  preview,
  sessionId,
  saving = false,
  savedResults = null,
  onClose,
  onSaved,
  onUndone,
  onPreviewFolded,
}: {
  visible: boolean;
  preview: ImportPreview | null;
  sessionId: string;
  /** Parent-driven save in flight (the bar's Apply path) — disables the footer. */
  saving?: boolean;
  /** When set, the draft is already committed: the footer becomes [Undo]. */
  savedResults?: { group_id: number } | null;
  onClose: () => void;
  onSaved: (results: ImportSaveOutcome) => void;
  onUndone: () => void;
  /** Each server-folded edit lands here: the parent swaps its preview state so
   * a close→reopen (no reload) re-seeds from the EDITED rows, not the stale
   * pre-edit copy it originally held. */
  onPreviewFolded?: (preview: ImportPreview) => void;
}) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const [rows, setRows] = useState<RowState[]>([]);
  const [drafts, setDrafts] = useState<Record<string, RowDraft>>({});
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [tab, setTab] = useState<'positions' | 'trades'>('positions');
  const [confirmPending, setConfirmPending] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Local drafts re-seeded each time the sheet opens (deliberately not while
  // open — the preview prop round-trips through the server, so rebinding
  // mid-edit would yank rows out from under the user, same as the thesis sheet).
  useEffect(() => {
    if (!visible) return;
    setRows(preview ? seedRows(preview) : []);
    setDrafts({});
    setExpandedId(null);
    setTab('positions');
    setConfirmPending(false);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  /** Fire-and-forget edit: the optimistic local update already happened, so a
   * failure only means this edit may not survive a reload — warn + surface
   * inline, never block the sheet. A later success clears a stale error. The
   * success response carries the server-folded preview: hand it to the parent
   * so a reopen re-seeds from the edited rows (H3). */
  const fireEdit = (rowId: string, patch: ImportRowPatch, include?: boolean) => {
    postImportEdit(sessionId, rowId, patch, include)
      .then((resp) => {
        setError(null);
        if (resp?.preview) onPreviewFolded?.(resp.preview);
      })
      .catch((e: unknown) => {
        console.warn('[ImportReviewSheet] import edit failed', e);
        setError(
          t('chat.import.editFailed', {
            defaultValue: 'Edit may not have synced — it can be lost on reload',
          })
        );
      });
  };

  const expandRow = (rowId: string) => {
    if (expandedId === rowId) {
      setExpandedId(null);
      return;
    }
    const r = rows.find((x) => x.row_id === rowId);
    if (r) setDrafts((prev) => ({ ...prev, [rowId]: seedDraft(r) }));
    setExpandedId(rowId);
  };

  const commitField = (rowId: string, field: EditableField) => {
    const r = rows.find((x) => x.row_id === rowId);
    if (!r) return;
    const raw = (drafts[rowId]?.[field] ?? '').trim();
    let value: string | number | null = null; // empty = explicit clear
    if (raw !== '') {
      if (field === 'quantity' || field === 'price') {
        const n = Number(raw);
        if (!Number.isFinite(n) || n < 0) return; // junk: keep the last good value
        value = n;
      } else {
        value = raw;
      }
    }
    if (r[field] === value) return; // no-op
    setRows((prev) =>
      prev.map((x) => (x.row_id === rowId ? ({ ...x, [field]: value } as RowState) : x))
    );
    fireEdit(rowId, { [field]: value } as ImportRowPatch, r.include);
  };

  const toggleInclude = (rowId: string) => {
    const r = rows.find((x) => x.row_id === rowId);
    if (!r) return;
    const next = !r.include;
    // A toggle can flip the H2 gate's precondition: when the include-set no
    // longer holds any update row, an armed confirmation is stale — disarm it
    // (the footer must not offer "incl. corrections" with none in the set).
    const stillNeedsGate = rows.some(
      (x) => x.suggestion === 'update' && (x.row_id === rowId ? next : x.include)
    );
    if (!stillNeedsGate) setConfirmPending(false);
    setRows((prev) => prev.map((x) => (x.row_id === rowId ? { ...x, include: next } : x)));
    fireEdit(rowId, {}, next);
  };

  const setResolution = (rowId: string, resolution: Resolution) => {
    const current = rows.find((x) => x.row_id === rowId);
    setRows((prev) => prev.map((x) => (x.row_id === rowId ? { ...x, resolution } : x)));
    fireEdit(rowId, { resolution_default: resolution }, current?.include);
  };

  const included = rows.filter((r) => r.include);
  const flagged = preview ? flaggedCount(preview) : 0;
  // H2 gate: any included ⇄ row means the apply records a correction.
  const needsGate = included.some((r) => r.suggestion === 'update');
  const footerLocked = saving || busy;

  const saveRows = (): ImportSaveRow[] =>
    included.map((r) => {
      const out: ImportSaveRow = { row_id: r.row_id, include: true };
      if (r.suggestion === 'update') out.resolution_action = r.resolution;
      if (r.symbol != null) out.symbol = r.symbol;
      if (r.quantity != null) out.quantity = r.quantity;
      if (r.price != null) out.price = r.price;
      if (r.currency != null) out.currency = r.currency;
      if (r.date != null) out.date = r.date;
      if (r.instrument_id != null) out.instrument_id = r.instrument_id;
      return out;
    });

  const doSave = async () => {
    if (!preview || footerLocked) return;
    setBusy(true);
    try {
      const results = await postImportSave(sessionId, preview.group_id, saveRows());
      setError(null); // a successful save supersedes any stale inline error
      onSaved(results);
    } catch (e) {
      console.warn('[ImportReviewSheet] import save failed', e);
      setError(
        t('chat.import.saveFailed', {
          defaultValue: 'Could not apply — check your connection and retry',
        })
      );
    } finally {
      setBusy(false);
      setConfirmPending(false);
    }
  };

  const doUndo = async () => {
    if (!savedResults || footerLocked) return;
    setBusy(true);
    try {
      await postImportUndo(sessionId, savedResults.group_id);
      onUndone();
    } catch (e) {
      console.warn('[ImportReviewSheet] import undo failed', e);
      setError(t('chat.import.undoFailed', { defaultValue: 'Undo failed — retry' }));
    } finally {
      setBusy(false);
    }
  };

  const onApplyPress = () => {
    if (included.length === 0 || footerLocked) return;
    if (needsGate && !confirmPending) {
      setConfirmPending(true); // first tap: swap the footer for the confirmation
      return;
    }
    void doSave();
  };

  const renderRow = (r: RowState) => {
    const expanded = expandedId === r.row_id;
    return (
      <View
        key={r.row_id}
        testID={`import-row-${r.row_id}`}
        style={[styles.row, { borderColor: theme.colors.border }]}
      >
        <Pressable
          testID="import-row-expand"
          onPress={() => expandRow(r.row_id)}
          accessibilityRole="button"
          accessibilityState={{ expanded }}
          style={styles.rowHead}
        >
          <Text style={[styles.chipIcon, { color: theme.colors.text }]}>
            {CHIP_ICON[r.suggestion]}
          </Text>
          <Text style={[styles.chipLabel, { color: theme.colors.textSecondary }]}>
            {t(`chat.import.chip.${r.suggestion}`, {
              defaultValue: CHIP_DEFAULT[r.suggestion],
            })}
          </Text>
          <Text style={[styles.symbol, { color: theme.colors.text }]}>{r.symbol ?? '—'}</Text>
          <Text style={[styles.qty, { color: theme.colors.textSecondary }]}>
            {fmtQty(r.quantity)}
          </Text>
          <Text style={[styles.chevron, { color: theme.colors.textTertiary }]}>
            {expanded ? '▴' : '▾'}
          </Text>
        </Pressable>

        {expanded ? (
          <View style={styles.rowBody}>
            {r.suggestion === 'update' ? (
              <>
                <Text
                  testID={`import-row-${r.row_id}-diff`}
                  style={[styles.diff, { color: theme.colors.text }]}
                >
                  {fmtQty(r.existing_quantity)}→{fmtQty(r.quantity)}
                </Text>
                {/* Resolution toggle — the two semantics record materially
                    different trades, so the copy states the consequence. */}
                <TouchableOpacity
                  testID={`import-row-${r.row_id}-res-replace`}
                  style={[
                    styles.resolution,
                    { borderColor: theme.colors.border },
                    r.resolution === 'replace' && { borderColor: theme.colors.primary },
                  ]}
                  onPress={() => setResolution(r.row_id, 'replace')}
                  accessibilityRole="button"
                  accessibilityState={{ selected: r.resolution === 'replace' }}
                >
                  <Text
                    style={[
                      styles.resolutionText,
                      {
                        color:
                          r.resolution === 'replace' ? theme.colors.primary : theme.colors.text,
                      },
                    ]}
                  >
                    {t('chat.import.replace', {
                      defaultValue: 'Correction — adjust my book by {{delta}}',
                      delta: fmtDelta(r.qty_delta),
                    })}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  testID={`import-row-${r.row_id}-res-add`}
                  style={[
                    styles.resolution,
                    { borderColor: theme.colors.border },
                    r.resolution === 'add' && { borderColor: theme.colors.primary },
                  ]}
                  onPress={() => setResolution(r.row_id, 'add')}
                  accessibilityRole="button"
                  accessibilityState={{ selected: r.resolution === 'add' }}
                >
                  <Text
                    style={[
                      styles.resolutionText,
                      { color: r.resolution === 'add' ? theme.colors.primary : theme.colors.text },
                    ]}
                  >
                    {t('chat.import.add', {
                      defaultValue: 'New purchase — stack {{qty}} as a new lot',
                      qty: fmtQty(r.quantity),
                    })}
                  </Text>
                </TouchableOpacity>
              </>
            ) : null}

            {EDIT_FIELDS.map((field) => (
              <View key={field} style={styles.field}>
                <Text style={[styles.fieldLabel, { color: theme.colors.textSecondary }]}>
                  {t(`chat.import.field.${field}`, { defaultValue: field })}
                </Text>
                <TextInput
                  testID={`import-row-${r.row_id}-field-${field}`}
                  style={[
                    styles.input,
                    {
                      backgroundColor: theme.colors.backgroundElevated,
                      borderColor: theme.colors.border,
                      color: theme.colors.text,
                    },
                  ]}
                  value={drafts[r.row_id]?.[field] ?? ''}
                  onChangeText={(text) =>
                    setDrafts((prev) => ({
                      ...prev,
                      [r.row_id]: { ...prev[r.row_id], [field]: text } as RowDraft,
                    }))
                  }
                  onEndEditing={() => commitField(r.row_id, field)}
                  keyboardType={
                    field === 'quantity' || field === 'price' ? 'decimal-pad' : 'default'
                  }
                  autoCapitalize={
                    field === 'symbol' || field === 'currency' ? 'characters' : 'none'
                  }
                  placeholder={field === 'date' ? 'YYYY-MM-DD' : undefined}
                  placeholderTextColor={theme.colors.textTertiary}
                />
              </View>
            ))}

            <TouchableOpacity
              testID={`import-row-${r.row_id}-include`}
              style={[
                styles.include,
                { borderColor: theme.colors.border },
                r.include && { borderColor: theme.colors.primary },
              ]}
              onPress={() => toggleInclude(r.row_id)}
              accessibilityRole="button"
              accessibilityState={{ selected: r.include }}
            >
              <Text
                style={[
                  styles.includeText,
                  { color: r.include ? theme.colors.primary : theme.colors.textSecondary },
                ]}
              >
                {r.include
                  ? t('chat.import.included', { defaultValue: '✓ Included — tap to exclude' })
                  : t('chat.import.excluded', { defaultValue: 'Excluded — tap to include' })}
              </Text>
            </TouchableOpacity>
          </View>
        ) : null}
      </View>
    );
  };

  const openRows = preview ? rows : [];
  const positionsRows = openRows.filter(
    (r) => r.kind === 'position' && r.suggestion !== 'needs_edit'
  );
  const tradesRows = openRows.filter((r) => r.kind === 'trade' && r.suggestion !== 'needs_edit');
  const flaggedRows = openRows.filter((r) => r.suggestion === 'needs_edit');
  const activeTabRows = tab === 'positions' ? positionsRows : tradesRows;

  return (
    <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <Pressable
          style={styles.backdropDismiss}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel={t('common.close', { defaultValue: 'Close' })}
        />
        <View style={[styles.sheet, { backgroundColor: theme.colors.surface }]}>
          <View style={[styles.handle, { backgroundColor: theme.colors.border }]} />
          <Text style={[styles.title, { color: theme.colors.text }]}>
            {t('chat.import.sheetTitle', { defaultValue: 'Review import' })}
          </Text>

          <ScrollView
            style={styles.scroll}
            contentContainerStyle={styles.scrollContent}
            keyboardShouldPersistTaps="handled"
          >
            {/* Pinned flagged section — above the tabs so a flagged row can
                never strand behind the tab it doesn't belong to. */}
            {flaggedRows.length > 0 ? (
              <View testID="import-sheet-needs-attention" style={styles.pinned}>
                <View style={styles.pinnedHeader}>
                  <Text style={[styles.pinnedHeaderIcon, { color: theme.colors.warningText }]}>
                    ⚠
                  </Text>
                  <Text style={[styles.pinnedHeaderText, { color: theme.colors.warningText }]}>
                    {t('chat.import.needsAttention', {
                      defaultValue: 'Needs attention ({{n}})',
                      n: flaggedRows.length,
                    })}
                  </Text>
                </View>
                {flaggedRows.map(renderRow)}
              </View>
            ) : null}

            <View style={styles.tabs}>
              <TouchableOpacity
                testID="import-sheet-tab-positions"
                style={[styles.tab, tab === 'positions' && { borderColor: theme.colors.primary }]}
                onPress={() => setTab('positions')}
                accessibilityRole="tab"
                accessibilityState={{ selected: tab === 'positions' }}
              >
                <Text
                  style={[
                    styles.tabText,
                    {
                      color:
                        tab === 'positions' ? theme.colors.primary : theme.colors.textSecondary,
                    },
                  ]}
                >
                  {t('chat.import.tabPositions', { defaultValue: 'Positions' })}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                testID="import-sheet-tab-trades"
                style={[styles.tab, tab === 'trades' && { borderColor: theme.colors.primary }]}
                onPress={() => setTab('trades')}
                accessibilityRole="tab"
                accessibilityState={{ selected: tab === 'trades' }}
              >
                <Text
                  style={[
                    styles.tabText,
                    { color: tab === 'trades' ? theme.colors.primary : theme.colors.textSecondary },
                  ]}
                >
                  {t('chat.import.tabTrades', { defaultValue: 'Trades' })}
                </Text>
              </TouchableOpacity>
            </View>

            <View testID={`import-sheet-pane-${tab}`}>{activeTabRows.map(renderRow)}</View>
          </ScrollView>

          {/* Footer: Undo receipt → gated Apply. The inline error line is
              non-blocking: edits applied locally even if the sync failed. */}
          {error ? (
            <Text testID="import-sheet-error" style={[styles.error, { color: theme.colors.error }]}>
              {error}
            </Text>
          ) : null}

          {savedResults ? (
            <View style={styles.footer}>
              <TouchableOpacity
                testID="import-undo"
                style={[styles.secondaryButton, { borderColor: theme.colors.primary }]}
                onPress={doUndo}
                disabled={footerLocked}
                accessibilityRole="button"
              >
                <Text style={[styles.primaryButtonText, { color: theme.colors.primary }]}>
                  [{t('actions.undo', { defaultValue: 'Undo' })}]
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                testID="import-sheet-close"
                style={[styles.secondaryButton, { borderColor: theme.colors.border }]}
                onPress={onClose}
                disabled={footerLocked}
                accessibilityRole="button"
              >
                <Text style={[styles.secondaryButtonText, { color: theme.colors.textSecondary }]}>
                  {t('common.close', { defaultValue: 'Close' })}
                </Text>
              </TouchableOpacity>
            </View>
          ) : confirmPending ? (
            <View testID="import-apply-confirm" style={styles.footer}>
              <Text style={[styles.confirmText, { color: theme.colors.text }]}>
                {t('chat.import.confirm', {
                  defaultValue: 'Records {{n}} changes incl. corrections — continue?',
                  n: included.length,
                })}
              </Text>
              <TouchableOpacity
                testID="import-cancel"
                style={[styles.secondaryButton, { borderColor: theme.colors.border }]}
                onPress={() => setConfirmPending(false)}
                accessibilityRole="button"
              >
                <Text style={[styles.secondaryButtonText, { color: theme.colors.textSecondary }]}>
                  {t('common.cancel', { defaultValue: 'Cancel' })}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                testID="import-continue"
                style={[styles.primaryButton, { backgroundColor: theme.colors.primary }]}
                onPress={doSave}
                disabled={footerLocked}
                accessibilityRole="button"
              >
                <Text style={[styles.primaryButtonText, { color: theme.colors.textInverse }]}>
                  {busy
                    ? t('common.saving', { defaultValue: 'Saving...' })
                    : t('chat.import.continue', { defaultValue: 'Continue' })}
                </Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.footer}>
              {flagged > 0 ? (
                <Text style={[styles.flagNote, { color: theme.colors.warningText }]}>
                  {`+${flagged} `}
                  {t('chat.import.needAttention', { defaultValue: 'need attention' })}
                </Text>
              ) : null}
              <TouchableOpacity
                testID="import-apply"
                style={[
                  styles.primaryButton,
                  { backgroundColor: theme.colors.primary },
                  included.length === 0 && styles.disabled,
                ]}
                onPress={onApplyPress}
                disabled={included.length === 0 || footerLocked}
                accessibilityRole="button"
                accessibilityState={{ disabled: included.length === 0 || footerLocked }}
              >
                <Text style={[styles.primaryButtonText, { color: theme.colors.textInverse }]}>
                  {busy
                    ? t('common.saving', { defaultValue: 'Saving...' })
                    : t('chat.import.apply', {
                        defaultValue: 'Apply {{n}} changes',
                        n: included.length,
                      })}
                </Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    backdrop: {
      flex: 1,
      justifyContent: 'flex-end',
      backgroundColor: theme.colors.overlay,
    },
    backdropDismiss: {
      ...StyleSheet.absoluteFillObject,
    },
    sheet: {
      maxHeight: '90%',
      borderTopLeftRadius: theme.radii.lg,
      borderTopRightRadius: theme.radii.lg,
      paddingTop: theme.spacing.xs,
      paddingBottom: theme.spacing.lg,
    },
    handle: {
      alignSelf: 'center',
      width: 40,
      height: 4,
      borderRadius: theme.radii.full,
      marginBottom: theme.spacing.sm,
    },
    title: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightBold,
      paddingHorizontal: theme.spacing.lg,
      marginBottom: theme.spacing.sm,
    },
    scroll: {
      flexGrow: 0,
      flexShrink: 1,
    },
    scrollContent: {
      paddingHorizontal: theme.spacing.lg,
      gap: theme.spacing.xs,
      paddingBottom: theme.spacing.md,
    },
    pinned: {
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderColor: theme.colors.warningBorder,
      borderRadius: theme.radii.md,
      padding: theme.spacing.sm,
      gap: theme.spacing.xs,
    },
    pinnedHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.xs,
    },
    pinnedHeaderIcon: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightBold,
    },
    pinnedHeaderText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightBold,
    },
    tabs: {
      flexDirection: 'row',
      gap: theme.spacing.sm,
      marginTop: theme.spacing.xxs,
    },
    tab: {
      flex: 1,
      alignItems: 'center',
      paddingVertical: theme.spacing.sm,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderColor: 'transparent',
    },
    tabText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    row: {
      borderWidth: 1,
      borderRadius: theme.radii.md,
      marginTop: theme.spacing.xxs,
    },
    rowHead: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.sm,
      padding: theme.spacing.sm,
    },
    chipIcon: {
      fontSize: theme.typography.fontSizeSm,
    },
    chipLabel: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    symbol: {
      flex: 1,
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightBold,
    },
    qty: {
      fontSize: theme.typography.fontSizeSm,
    },
    chevron: {
      fontSize: theme.typography.fontSizeSm,
    },
    rowBody: {
      paddingHorizontal: theme.spacing.sm,
      paddingBottom: theme.spacing.sm,
      gap: theme.spacing.xs,
    },
    diff: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightBold,
    },
    resolution: {
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
      paddingHorizontal: theme.spacing.sm,
      paddingVertical: theme.spacing.sm,
    },
    resolutionText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    field: {
      gap: theme.spacing.xxs,
    },
    fieldLabel: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    input: {
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
      paddingHorizontal: theme.spacing.sm,
      paddingVertical: theme.spacing.xs,
      fontSize: theme.typography.fontSizeSm,
      minWidth: 0,
    },
    include: {
      alignItems: 'center',
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
      paddingVertical: theme.spacing.xs,
    },
    includeText: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    error: {
      paddingHorizontal: theme.spacing.lg,
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    footer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.md,
      paddingHorizontal: theme.spacing.lg,
      paddingTop: theme.spacing.md,
    },
    flagNote: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightBold,
      flexShrink: 1,
    },
    confirmText: {
      flex: 1,
      flexShrink: 1,
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    primaryButton: {
      flex: 2,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 48,
      borderRadius: theme.radii.md,
    },
    secondaryButton: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 48,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    primaryButtonText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightBold,
    },
    secondaryButtonText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    disabled: {
      opacity: 0.5,
    },
  });
}
